import { IconInfoSolid } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { Typography } from '@common/styles';
import { numberWithCommas } from '@common/utils';
import { DiscountInfo, ProductPrice } from '@domains/pdp-common';

import { GetDiscountPopup_pdp_base_info_product_product_price } from '../../../graphql/get-discount-popup_types.2';
import { PriceReceipt } from './PriceReceipt';

interface PriceInfoProps {
  productPrice: ProductPrice | GetDiscountPopup_pdp_base_info_product_product_price;
  discountInfo: DiscountInfo | null;
}

export const PriceInfo: React.FC<React.PropsWithChildren<PriceInfoProps>> = ({
  productPrice: {
    display_final_price: {
      final_price: { badge: finalPriceBadge },
      final_price_additional,
    },
    store_discount_info,
    first_order_discount,
    final_discount_info: { discount_price: finalDiscountPrice },
    max_price_info: { price: maxPrice },
    coupon_discount_info_list,
    bogo_promotion_discount_info,
    product_promotion_discount_info,
  },
  discountInfo,
}) => {
  const storeDiscountAmount = store_discount_info?.discount_amount ?? 0;
  const bogoPromotionAmount = bogo_promotion_discount_info?.discount_amount ?? 0;
  const promotionDiscountAmount = product_promotion_discount_info?.discount_amount ?? 0;

  const badgeText = finalPriceBadge?.text || final_price_additional?.badge?.text;
  const displayChallenge = badgeText === '최저가도전';
  const isMaxPrice = maxPrice > 0;
  const isBasicDiscount = storeDiscountAmount > 0;
  const isFirstOrderDiscount = first_order_discount && first_order_discount?.discount_amount > 0;
  const isBogoPromotionDiscount = bogoPromotionAmount > 0;
  const isPromotionDiscount = promotionDiscountAmount > 0;

  return (
    <div>
      <InfoTitle className={Typography.BODY_16_SEMIBOLD}>{'할인가 자세히 보기'}</InfoTitle>
      {displayChallenge && (
        <LowestPriceChallenge>
          <ChallengeTitle>
            <IconInfoSolid className='icon' width={20} v3Color='pink400' />
            최저가도전이란?
          </ChallengeTitle>
          <ChallengeDescription>
            동일 상품의 외부 시장 가격을 데일리로 모니터링하여 경쟁력 있는 가격으로 제공합니다. 단, 대상 상품의 선정 및
            가격 모니터링은 지그재그의 기준에 따르며, 이 가격은 시장 상황에 따라 변동될 수 있습니다.
          </ChallengeDescription>
        </LowestPriceChallenge>
      )}
      {isMaxPrice && <PriceReceipt title={'판매 가격'} title_color={vars.v3Color.gray500} price={maxPrice} />}
      {isBasicDiscount && (
        <PriceReceipt
          title='스토어 할인'
          title_color={vars.v3Color.gray400}
          price_rate_text={`${store_discount_info?.discount_rate}% 할인`}
          price_rate_color={'#BD8080'}
          price={storeDiscountAmount}
          is_sale={true}
        />
      )}
      {isPromotionDiscount && (
        <PriceReceipt
          title={'지그재그 추가 할인'}
          title_color={vars.v3Color.gray900}
          price_image_url={discountInfo ? discountInfo.image_url : null}
          price_rate_text={`${Math.round((promotionDiscountAmount / maxPrice) * 100)}% 추가 할인`}
          price={promotionDiscountAmount}
          is_sale={true}
        />
      )}
      {isBogoPromotionDiscount && (
        <PriceReceipt
          title={'1+1 할인'}
          title_color={vars.v3Color.gray900}
          price_image_url={discountInfo ? discountInfo.image_url : null}
          price_rate_text={`${Math.round((bogoPromotionAmount / maxPrice) * 100)}% 추가 할인`}
          price={bogoPromotionAmount}
          is_sale={true}
        />
      )}
      {coupon_discount_info_list?.map((coupon) => {
        const title = coupon.title?.text ?? '';
        const couponDiscountAmount = coupon.discount_amount;
        const couponPrice = maxPrice - storeDiscountAmount - (couponDiscountAmount ?? 0);

        const isCouponDiscount = couponDiscountAmount > 0;
        const isZeroOrNegative = couponPrice <= 0;

        if (isCouponDiscount && !isZeroOrNegative) {
          return (
            <PriceReceipt
              key={title}
              title={title}
              title_color={vars.v3Color.gray900}
              price_image_url={discountInfo ? discountInfo.image_url : null}
              price_rate_text={`${(coupon?.display_discount_rate_bp || 0) / 100}% 추가 할인`}
              price={couponDiscountAmount}
              is_sale={true}
            />
          );
        }
        return null;
      })}

      {isFirstOrderDiscount && (
        <PriceReceipt
          title={'첫 구매 할인'}
          title_color={vars.v3Color.gray900}
          price_image_url={discountInfo ? discountInfo.image_url : null}
          price_rate_text={`${Math.round((first_order_discount.discount_amount / maxPrice) * 100)}% 추가 할인`}
          price={first_order_discount.discount_amount}
          is_sale={true}
        />
      )}
      <FinalPriceWrapper>
        <span className={Typography.BODY_15_MEDIUM}>{'최종 상품 금액'}</span>
        <PriceValue className={Typography.BODY_17_SEMIBOLD}>{numberWithCommas(finalDiscountPrice)}</PriceValue>
      </FinalPriceWrapper>
    </div>
  );
};

const InfoTitle = styled.h3`
  color: ${vars.v3Color.gray950};
  text-align: center;
`;

const FinalPriceWrapper = styled.div`
  height: 42px;
  padding: 12px 16px;
  margin: 10px 0 0 0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  color: ${vars.v3Color.gray900};
  background-color: ${vars.v3Color.gray100};
`;

const PriceValue = styled.span`
  margin-left: auto;
`;

const LowestPriceChallenge = styled.div`
  margin: 16px 0 8px 0;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-items: center;
  border-radius: 4px;
  background-color: #f5f6f8;
`;

const ChallengeTitle = styled.div`
  margin: 0 0 10px -4px;
  height: 18px;
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  line-height: 18px;
  color: ${vars.v3Color.pink400};

  .icon {
    margin: 0 2px 0 0;
  }
`;

const ChallengeDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 165%;
  word-break: keep-all;
  color: ${vars.v3Color.gray900};

  span {
    font-size: 14px;
    font-weight: 600;
    line-height: 21px;
  }
`;
